import React, { useState, useCallback } from "react";
import { Accordion, Button, Form } from "react-bootstrap";
import {
  FaCheckCircle,
  FaClock,
  FaCommentDots,
  FaExclamationCircle,
  FaHeadset,
  FaPaperPlane,
  FaPhone,
  FaPhoneAlt,
  FaPuzzlePiece,
  FaQuestionCircle,
  FaRedo,
  FaSearch,
  FaSearchMinus,
  FaTag,
  FaThumbsDown,
  FaThumbsUp,
} from "react-icons/fa";
import parse from "react-html-parser";

import { fetchFaqs } from "../../api/faq";
import "./FaqBanner.css";

import { ShimmerText } from "react-shimmer-effects";
import LivevoxChat from "../pdp/pdpComponents/LivevoxChat";
import { formatCmsTextToHtml } from "@/utils/textParser";
import FaqSearchIcon from "../../../public/assets/svg/faq/FaqSearchIcon";

// Reusable Feedback Components
const FeedbackConfirmation = () => (
  <div className="mt-4 feedback-confirmation animate-fade-in">
    <div className="faq-feedback-content">
      <FaCheckCircle size={30} color="#28a745" />
      <h6 className="feedback-message">Thank you for your feedback!</h6>
      <p className="feedback-subtext">Your response helps us improve</p>
    </div>
  </div>
);

const CustomerCareSection = ({ onReset }) => (
  <div className="customer-care-container">
    <div className="contact-info-card">
      <div className="icon-wrapper">
        <FaHeadset size={30} color="#0d6efd" />
      </div>
      <div className="contact-details">
        <h5 className="contact-title">Need Additional Help?</h5>
        <p className="contact-text">
          Our Customer Care team is available to assist you
        </p>
        <div className="contact-hours">
          <FaClock size={30} />
          <span>Monday - Friday, 7 AM - 7 PM EDT</span>
        </div>
        <div className="contact-phone">
          <FaPhone />
          <span>(855) 882-1314</span>
        </div>
      </div>
      <div className="buttons-wrapper">
        <Button
          variant="outline-primary"
          className="fb-action-btn fb-search-btn"
          onClick={onReset}
        >
          <div className="fb-btn-content">
            <FaSearch className="fb-btn-content-icon" />
            <span>Search Again</span>
          </div>
        </Button>
        <Button variant="outline-primary" className="fb-action-btn fb-chat-btn">
          <div className="fb-btn-content">
            <FaCommentDots className="fb-btn-content-icon" />
            {/* <span>Chat With Customer Care</span> */}
            <LivevoxChat
              showContent={false}
              contentText="Chat With Customer Care"
              style={{ color: "#fff", marginLeft: "8px" }}
            />
          </div>
        </Button>
      </div>
    </div>
  </div>
);

const NegativeFeedbackForm = ({ onReasonSelect, onSubmit, selectedReason }) => (
  <div className="negaitve-feedback-section">
    <div className="negaitve-feedback-header">
      <h6>What best describes the information we provided?</h6>
      <p className="negaitve-feedback-subtitle">
        Your feedback helps us improve our answers
      </p>
    </div>
    <Form className="negaitve-feedback-form">
      <div className="negaitve-feedback-options">
        <div
          className={`negaitve-feedback-option ${
            selectedReason === "confusing" ? "selected" : ""
          }`}
          onClick={() => onReasonSelect("confusing")}
        >
          <FaPuzzlePiece />
          <span>This information is confusing</span>
        </div>
        <div
          className={`negaitve-feedback-option ${
            selectedReason === "not-looking-for" ? "selected" : ""
          }`}
          onClick={() => onReasonSelect("not-looking-for")}
        >
          <FaSearchMinus />
          <span>This is not what I was looking for</span>
        </div>
      </div>
      <Button
        variant="primary"
        className="feedback-submit-button"
        onClick={onSubmit}
        disabled={!selectedReason}
      >
        <FaPaperPlane />
        Submit Feedback
      </Button>
    </Form>
  </div>
);

const FaqBanner = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [feedback, setFeedback] = useState({});

  const handleSearch = useCallback(
    async (e, popular) => {
      e.preventDefault();
      if (!searchQuery && !popular) return;

      setLoading(true);
      setError("");
      setResults([]);

      try {
        const response = await fetchFaqs(popular || searchQuery);

        if (response?.result?.faqList?.length > 0) {
          setResults(response.result.faqList);
        } else {
          setError("No data available for your query.");
          setResults([]);
        }
      } catch (err) {
        setError("Failed to fetch results. Please try again later.");
      } finally {
        setLoading(false);
      }
    },
    [searchQuery]
  );

  const handleFeedbackUpdate = useCallback((id, updates) => {
    setFeedback((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        ...updates,
      },
    }));
  }, []);

  const handleReset = useCallback(() => {
    setResults([]);
    setSearchQuery("");
    setFeedback({});
    window.scroll(0, 0);
  }, []);

  const renderFeedbackSection = useCallback(
    (item) => {
      const fb = feedback[item.id] || {};

      if (fb.helpful) {
        return <FeedbackConfirmation />;
      }

      if (fb.callCustomerCare) {
        return <CustomerCareSection onReset={handleReset} />;
      }

      if (fb.submitted) {
        return (
          <div className="feedback-success-container">
            <div className="feedback-card">
              <div className="feedback-header">
                <div className="success-icon-wrapper">
                  <FaCheckCircle size={30} color="#28a745" />
                </div>
                <h5 className="feedback-title">Thank You for Your Feedback!</h5>
                <p className="feedback-subtitle">
                  Your input helps us improve our help content and serve you
                  better
                </p>
              </div>
              <div className="support-options">
                <h6 className="options-title">
                  What would you like to do next?
                </h6>
                <div className="buttons-wrapper">
                  <Button
                    variant="outline-primary"
                    className="fb-action-btn fb-search-btn"
                    onClick={handleReset}
                  >
                    <div className="fb-btn-content">
                      <FaSearch className="fb-btn-content-icon" />
                      <span>Search Again</span>
                    </div>
                  </Button>
                  <Button
                    variant="outline-primary"
                    className="fb-action-btn fb-chat-btn"
                  >
                    <div className="fb-btn-content">
                      <FaCommentDots className="fb-btn-content-icon" />
                      {/* <span>Chat With Customer Care</span> */}
                      <LivevoxChat
                        showContent={false}
                        contentText="Chat With Customer Care"
                        style={{ color: "#fff", marginLeft: "8px" }}
                      />
                    </div>
                  </Button>
                  <Button
                    variant="outline-primary"
                    className="fb-action-btn fb-call-btn"
                    onClick={() =>
                      handleFeedbackUpdate(item.id, { callCustomerCare: true })
                    }
                  >
                    <div className="fb-btn-content">
                      <FaPhoneAlt className="fb-btn-content-icon" />
                      <span>Speak With Customer Care</span>
                    </div>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        );
      }

      if (fb.helpful === false) {
        return (
          <NegativeFeedbackForm
            onReasonSelect={(reason) =>
              handleFeedbackUpdate(item.id, { reason })
            }
            onSubmit={() => handleFeedbackUpdate(item.id, { submitted: true })}
            selectedReason={fb.reason}
          />
        );
      }

      return (
        <div className="feedback-prompt">
          <div className="feedback-question">
            <h6>Was this information helpful?</h6>
            <p className="feedback-subtitle">Help us improve our support</p>
          </div>
          <div className="feedback-buttons">
            <Button
              className="feedback-btn yes-btn"
              onClick={() => handleFeedbackUpdate(item.id, { helpful: true })}
            >
              <FaThumbsUp size={14} />
              <span>Yes, it helped</span>
            </Button>
            <Button
              className="feedback-btn no-btn"
              onClick={() => handleFeedbackUpdate(item.id, { helpful: false })}
            >
              <FaThumbsDown size={14} />
              <span>No, not helpful</span>
            </Button>
          </div>
        </div>
      );
    },
    [feedback, handleFeedbackUpdate, handleReset]
  );

  return (
    <div className="faq-banner">
      <section className="inner_page_faq bg-light py-5">
        {/* <div className="container"> */}
        <div className="search-container">
          <div className="search-content">
            <h1 className="search-title">
              What can we help you with today?
              <span className="title-accent"></span>
            </h1>
            <div className="search-description">
              Search our help library below.
            </div>
            <div className="faq-search-container">
              <Form className="search-form" onSubmit={handleSearch}>
                <div className="search-input-wrapper">
                  {/* <FaQuestionCircle size={30} className="input-icon" /> */}
                  <Form.Control
                    type="text"
                    placeholder='Type your question here... e.g., "What is a foreclosure?"'
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="faq-search-input"
                    aria-label="Search for FAQs"
                  />
                  <Button type="submit" className="search-button" aria-label="search-button">
                    {/* <FaSearch size={24} className="search-button-icon" /> */}
                    <FaqSearchIcon
                      height={24}
                      width={24}
                      className="search-button-icon"
                    />
                    {/* <span>Search</span> */}
                  </Button>
                </div>
                <div className="popular-searches">
                  <span>Popular:</span>
                  {["Foreclosure", "Auction", "Property Inspection"].map(
                    (query) => (
                      <button
                        key={query}
                        type="button"
                        className="quick-search-tag"
                        onClick={(e) => {
                          setSearchQuery(query);
                          handleSearch(e, query);
                        }}
                      >
                        {query}
                      </button>
                    )
                  )}
                </div>
              </Form>
            </div>
          </div>
        </div>
        {/* {!loading && (
            <div className="loader-container">
              <div className="loader-content">
                <div className="loader-animation">
                  <Loader />
                </div>
                <p className="loader-text">Searching for answers...</p>
              </div>
            </div>
          )} */}
        {loading && (
          <div
            style={{
              width: "100%",
              padding: "0 12rem",
            }}
          >
            <h4 className="loading-text" style={{ color: "#000" }}>
              Loading helpful answers...
            </h4>
            <ShimmerText line={10} gap={20} />;
          </div>
        )}
        {error && (
          <div className="error-container animate-fade-in">
            <div className="error-content">
              <div className="error-icon">
                <FaExclamationCircle />
              </div>
              <div className="error-message">
                <h6 className="error-title">Oops! Something went wrong</h6>
                <p className="error-text">{error}</p>
              </div>
              <button
                className="error-retry-btn"
                onClick={() => window.location.reload()}
              >
                <FaRedo />
                Try Again
              </button>
            </div>
          </div>
        )}
        {results.length > 0 && (
          <div className="results-container">
            <div className="results-header">
              <h5 className="results-title">
                Found {results.length} helpful answers
              </h5>
              <p className="results-subtitle">
                Click on a question to see the answer
              </p>
            </div>
            <Accordion className="custom-accordion">
              {results.map((item, index) => (
                <Accordion.Item
                  key={item.id}
                  eventKey={index}
                  className="accordion-item-custom"
                >
                  <Accordion.Header className="accordion-header-custom">
                    <div className="question-content">
                      <div className="question-icon">
                        <FaQuestionCircle />
                      </div>
                      <div className="question-text">
                        <strong className="question-title">
                          {item.question}
                        </strong>
                        <span className="question-category">
                          <FaTag />
                          {item.category}
                        </span>
                      </div>
                    </div>
                  </Accordion.Header>
                  <Accordion.Body className="accordion-body-custom">
                    <div className="answer-content">
                      <p className="answer-text">
                        {parse(formatCmsTextToHtml(item.answer))}
                      </p>
                      <div className="answer-feedback">
                        {renderFeedbackSection(item)}
                      </div>
                    </div>
                  </Accordion.Body>
                </Accordion.Item>
              ))}
            </Accordion>
          </div>
        )}
        {/* </div> */}
      </section>
    </div>
  );
};

export default FaqBanner;
